/**
 * §17.1 / §18.5 — *"Last changed 4 Aug."*, the profile-freshness label.
 *
 * ── Why this is in `ui-b/` and not beside `teamProfileFacts` in `domain/` ───
 *
 * The **derivation** — `max(updatedAt)` over the active players and the shape — is pure and lives
 * in `domain/teamProfile.ts` where both readers get the same answer. The **label** cannot: turning
 * an epoch millisecond into a calendar day reads the host time zone, so the same timestamp renders
 * as two different days on two devices. `eslint.config.js`'s `pureClockSelectors` rail bans
 * `new Date(...)` inside `domain/` for exactly that reason (NFR-6/INV-7: *"a pure module may not
 * read a clock"*), and it is right — a hidden input is a hidden input whether it is the current
 * time or the current zone.
 *
 * So the split is: domain answers *when*, this answers *how to say it*, and the rail stays intact
 * rather than being suppressed.
 *
 * Two readers, one module: the My Team tab's quiet line and the disclosure above Generate.
 */

/** Abbreviated month names, index-aligned to `Date.prototype.getMonth()`. */
const MONTHS: readonly string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Day and abbreviated month in the **device's own** zone — *"4 Aug"* — **and the year whenever the
 * stamp is not in the reader's current year** — *"4 Sep 2025"*.
 *
 * ── Why the year, and why only sometimes (milestone review B-1) ──────────────
 *
 * A day and a month with no year is not a date; it is a date **modulo twelve months**. A profile
 * last edited 4 September 2025, read on 18 August 2026, rendered *"Last changed 4 Sep"* — which
 * reads as seventeen days in the **future**, not eleven months in the past. Everything roughly one
 * to twelve months ahead of today reads as a date that has not happened; everything older than a
 * year reads as recent. A youth roster is a season-long artifact — *"Add your squad once and every
 * match after that reuses it"* — so the season boundary is exactly where this bites, and it bites
 * silently, because a wrong date and a right one look identical.
 *
 * The year is appended **only** when the stamp falls outside the current year. §17.1 is explicit
 * that this line is *never a warning and never a gate*, and the in-year case is the overwhelming
 * common one: four characters of unread noise on every glance, every match, to disambiguate a case
 * that is not ambiguous, would be paying for the fix in the wrong place.
 *
 * ── Why `now` is a parameter and not a `Date.now()` in here ──────────────────
 *
 * It is the house form — `undoCountdown(lastIncrementTs, now)` two directories away,
 * `reorderAllowed(gate, now)`, `resumedMinute(match, now)`, all of them fed `Date.now()` at the
 * call site. Reading the clock in here would make the label's output depend on an input that does
 * not appear in its signature, which is the same class of hidden input the module header already
 * argues against for the time **zone**. Both callers are render paths that may read the clock; a
 * pure function that cannot is the half worth protecting.
 *
 * @param lastChanged epoch milliseconds, or `null` when nothing has ever been written.
 * @param now epoch milliseconds for "today", supplied by the caller — the year comparison is made
 *   in the device's own zone, like the day and month.
 * @returns the label, or `null` — which every caller renders as **no line at all** rather than as
 *   a placeholder. §17.1 is explicit that the freshness line is *never a warning and never a gate*,
 *   and an em dash where a date should be reads as both.
 */
export function freshnessLabel(lastChanged: number | null, now: number): string | null {
  if (lastChanged === null || lastChanged <= 0) return null;
  const date = new Date(lastChanged);
  const month = MONTHS[date.getMonth()];
  if (Number.isNaN(date.getTime()) || month === undefined) return null;
  const day = `${String(date.getDate())} ${month}`;
  const today = new Date(now);
  // An unusable `now` is not a reason to withhold the date; it is a reason not to claim the stamp
  // is in the current year. Falling back to "always print the year" keeps the label honest.
  if (Number.isNaN(today.getTime())) return `${day} ${String(date.getFullYear())}`;
  return date.getFullYear() === today.getFullYear()
    ? day
    : `${day} ${String(date.getFullYear())}`;
}
